import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

import { Stepper, type StepKey } from "./Stepper";
import { TokenGate } from "./TokenGate";
import { ProjectsMenu } from "@/features/projects/ProjectsMenu";
import { useProjectStore } from "@/features/projects/useProjectStore";

type AppShellProps = { children: ReactNode };

/**
 * Layout chrome for every authenticated route — dark indigo sticky header
 * with the brand mark + version, the 3-step stepper, and the centred
 * 1200px content container. Mirrors the existing legacy ``/`` template
 * 1:1 so the strangler-fig cutover is visually invisible.
 */
const STEP_PATHS: Record<StepKey, string> = {
  1: "donesi",
  2: "provjeri",
  3: "preuzmi",
};

export function AppShell({ children }: AppShellProps) {
  const { pathname } = useLocation();
  const step = stepFromPath(pathname);
  const projectId = projectIdFromPath(pathname);
  // Steps the user has already reached are navigable; future ones stay
  // inert until the flow unlocks them (currentStep tracks the max).
  const maxStep = useProjectStore(
    (s) => s.projects.find((p) => p.id === projectId)?.currentStep ?? 1,
  );

  return (
    <>
      <header className="sticky top-0 z-50 bg-ind-950 py-[0.85rem]">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-6">
          <Link
            to="/"
            className="flex items-baseline gap-3 no-underline"
          >
            <span className="text-[1.25rem] font-bold uppercase tracking-[0.12em] text-white">
              PERMIT<span className="text-amber-500">KO</span>
            </span>
            <span className="hidden text-[0.78rem] italic tracking-[0.02em] text-white/40 md:inline">
              Gdje nacrt postaje projekt.
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <ProjectsMenu />
            <Link
              to="/radar"
              className="text-[0.78rem] font-medium text-white/60 no-underline transition-colors hover:text-white"
            >
              Radar
            </Link>
            <Link
              to="/postavke"
              className="text-[0.78rem] font-medium text-white/60 no-underline transition-colors hover:text-white"
            >
              Postavke
            </Link>
            <span className="hidden text-[0.75rem] tracking-[0.04em] text-white/30 md:inline">
              v0.8 &middot; MVP
            </span>
          </div>
        </div>
      </header>

      {step !== null && (
        <Stepper
          current={step}
          hrefFor={(s) =>
            projectId && s <= maxStep
              ? `/project/${projectId}/${STEP_PATHS[s]}`
              : null
          }
        />
      )}

      <main className="mx-auto max-w-[1200px] px-6 pb-12 pt-6">
        <div className="pp-fadeIn">{children}</div>
      </main>

      <TokenGate />
    </>
  );
}

function projectIdFromPath(pathname: string): string | null {
  const m = /^\/project\/([^/]+)\//.exec(pathname);
  return m ? m[1] : null;
}

function stepFromPath(pathname: string): StepKey | null {
  if (
    pathname === "/" ||
    pathname === "" ||
    pathname.startsWith("/_kitchen") ||
    pathname.startsWith("/provjera") ||
    pathname.startsWith("/postavke")
  ) {
    return null;
  }
  // Legacy paths (/data, /feasibility, /docs) redirect to the new flow but
  // may flash through the stepper — map them to the equivalent step.
  if (
    pathname.endsWith("/donesi") ||
    pathname.endsWith("/data") ||
    pathname.endsWith("/feasibility")
  ) {
    return 1;
  }
  if (pathname.endsWith("/provjeri")) return 2;
  if (pathname.endsWith("/preuzmi") || pathname.endsWith("/docs")) return 3;
  return null;
}
